// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyOverviewResponse, TyStatsRange, TySubscriptionPeriod } from "@/types";
import AttentionRow from "./AttentionRow";
import CouponsSection from "./CouponsSection";
import HealthSection from "./HealthSection";
import Loader from "./Loader";
import MoneySection from "./MoneySection";
import RangeBar from "./RangeBar";
import StoresSection from "./StoresSection";
import SupportSection from "./SupportSection";
import { firstName } from "./helpers";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/** What the period control offers before the first response has told us what the API accepts. */
const FALLBACK_RANGES: TyStatsRange[] = ["today", "7d", "30d", "90d", "12m", "all"];

const DEFAULT_RANGE: TyStatsRange = "30d";

// --- Logic --------------------------------------------------------------------------------------

function pickGreeting(t: ReturnType<typeof useTranslation>): string {
    const hour = new Date().getHours();
    if (hour < 12) return t.home.greetingMorning;
    if (hour < 18) return t.home.greetingAfternoon;
    return t.home.greetingEvening;
}

/**
 * The whole state of Menuket on one screen, cut by one period control. Every figure comes from a single
 * `/home` call rather than one request per card — six calls each resolving the window on their own is how
 * a dashboard ends up with a revenue figure and a chart describing different fortnights.
 *
 * Which sections appear is the API's decision, not this screen's: it computes only what the caller's
 * permissions cover, so a missing key here means "not allowed", not "nothing to show".
 */
export default function HomeScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const admin = useAuth((state) => state.admin);

    const [range, setRange] = useState<TyStatsRange>(DEFAULT_RANGE);
    const [period, setPeriod] = useState<TySubscriptionPeriod | "">("");

    const { data, loading, error, invoke } = useFetch<TyApiResponse<TyOverviewResponse>>("home", { method: "GET" }, false);

    // The hook re-runs on `invoke`, not when the path or query changes, so the filters ask for the refetch.
    useEffect(() => {
        invoke({ query: { range, period } });
    }, [range, period]);

    const greetingName = firstName(admin?.name);

    const overview = data?.data;
    const meta = overview?.meta;

    return (
        <div className="home-screen">
            <div className="home-header">
                <div className="home-header-text">
                    <p className="home-greeting">
                        {pickGreeting(t)}
                        {/* Arabic and English do not share a comma, and the Arabic one was being printed
                            in both — "Good evening، Ahmed" in an English session. */}
                        {greetingName ? `${lang === "en" ? ", " : "، "}${greetingName}` : ""}
                    </p>
                    <p className="home-subtitle">{t.home.subtitle}</p>
                </div>

                <RangeBar
                    ranges={meta?.ranges ?? FALLBACK_RANGES}
                    range={range}
                    onRangeChange={setRange}
                    periods={overview?.subscriptions ? (meta?.periods ?? undefined) : undefined}
                    period={period}
                    onPeriodChange={setPeriod}
                />
            </div>

            {/* Only the first load gets a skeleton — a period change keeps the old numbers on screen and
                dims them, because blanking a dashboard mid-read is worse than a stale second. The deferred
                fetch means the very first frame has neither data nor `loading` yet, so `!error` covers it. */}
            {!overview && !error && <Loader />}

            {error && !overview && (
                <div className="home-error">
                    <p className="home-error-text">{error}</p>
                    <button type="button" className="home-error-retry" onClick={() => invoke({ query: { range, period } })}>
                        {t.home.retry}
                    </button>
                </div>
            )}

            {overview && meta && (
                <div className="home-body" data-stale={loading}>
                    <AttentionRow data={overview} />

                    {overview.stores && <StoresSection data={overview.stores} meta={meta} />}
                    {overview.subscriptions && <MoneySection data={overview.subscriptions} meta={meta} />}
                    {overview.support && <SupportSection data={overview.support} meta={meta} />}
                    {overview.coupons && <CouponsSection data={overview.coupons} meta={meta} />}
                    {overview.logs && <HealthSection data={overview.logs} meta={meta} />}
                </div>
            )}
        </div>
    );
}
